import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import can from 'ember-can/helpers/can';
import { dropTask, restartableTask } from 'ember-concurrency';
import InfinityLoader from 'ember-infinity/components/infinity-loader';
import type InfinityService from 'ember-infinity/services/infinity';
import { pluralize } from 'ember-inflector';
import PowerSelect from 'ember-power-select/components/power-select';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and, eq, not, or } from 'ember-truth-helpers';
import GlobalFlowIcon from 'garaje/components/global-flow-icon';
import TruncateWithTooltip from 'garaje/components/truncate-with-tooltip';
import MessageBox from 'garaje/components/ui/classic/message-box';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import UiButton from 'garaje/components/ui-button';
import UiInputField from 'garaje/components/ui-input-field';
import ExtendedInfinityModel, { type PaginatedRecordArray } from 'garaje/infinity-models/v3-offset';
import type FieldSetting from 'garaje/models/field-setting';
import type FlowModel from 'garaje/models/flow';
import type Location from 'garaje/models/location';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import urlBuilder from 'garaje/utils/url-builder';
import { TrackedMap } from 'tracked-built-ins';

interface RetentionOption {
  value: number | 'custom';
  label: string;
}

const RETENTION_OPTIONS: RetentionOption[] = [
  { value: 30, label: '30 days after visit date' },
  { value: 90, label: '90 days after visit date' },
  { value: 180, label: '180 days after visit date' },
  { value: 365, label: '1 year after visit date' },
  { value: 'custom', label: 'Custom' },
];

const PRESET_VALUES = new Set([30, 90, 180, 365]);

interface EntryRetentionPeriodSignature {
  Args: {
    currentLocation: Location;
  };
}

export default class EntryRetentionPeriod extends Component<EntryRetentionPeriodSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare infinity: InfinityService;
  @service declare store: Store;
  @service declare ajaxFetch: AjaxFetchService;

  @tracked isOpen = false;
  @tracked selectedOption: RetentionOption | null = null;
  @tracked customDays = '';

  @tracked flows: PaginatedRecordArray<FlowModel> | null = null;
  @tracked selectedFlow: FlowModel | null = null;

  @tracked showDeleteModal = false;
  @tracked confirmDeleteText = '';

  @tracked fieldSettingsByFlow = new TrackedMap<string, FieldSetting[]>();

  get fieldSettings(): FieldSetting[] {
    // eslint-disable-next-line ember/use-ember-get-and-set
    return this.fieldSettingsByFlow.get(this.selectedFlow?.id ?? '') ?? [];
  }

  get customDaysIsNaN(): boolean {
    if (!this.isCustomSelected) return false;
    return isNaN(parseInt(this.customDays, 10));
  }

  get customDaysTooLow(): boolean {
    if (!this.isCustomSelected) return false;
    const days = parseInt(this.customDays, 10);
    return !isNaN(days) && days <= 0;
  }

  constructor(owner: unknown, args: EntryRetentionPeriodSignature['Args']) {
    super(owner, args);
    this.#initFromLocation();

    void this.loadFlowsTask.perform();
  }

  get errors(): string[] {
    const errors: string[] = [];
    if (this.customDaysIsNaN) {
      errors.push('Please enter a valid number');
    }
    if (this.customDaysTooLow) {
      errors.push('Retention period must be at least 1 day');
    }

    return errors;
  }

  get saveDisabled(): boolean {
    const isPristine = !this.hasDirtyFieldSettings && !this.args.currentLocation.hasDirtyAttributes;
    return this.saveTask.isRunning || Boolean(this.errors.length) || isPristine || this.showDeleteModal;
  }

  get indefinitely(): boolean {
    return !this.args.currentLocation.entryRetentionPeriod;
  }

  get isCustomSelected(): boolean {
    return this.selectedOption?.value === 'custom';
  }

  get retentionOptions(): RetentionOption[] {
    return RETENTION_OPTIONS;
  }

  get flowOptions(): FlowModel[] {
    return this.flows?.slice() ?? [];
  }

  get lastFlow(): FlowModel | null {
    return this.flowOptions.at(-1) ?? null;
  }

  get fieldSettingsHidden(): boolean {
    return this.indefinitely || this.args.currentLocation.redactAllVisitorData;
  }

  get hasDirtyFieldSettings(): boolean {
    if (this.fieldSettingsHidden) return false;

    for (const settings of this.fieldSettingsByFlow.values()) {
      if (settings.some((fs) => fs.hasDirtyAttributes)) return true;
    }
    return false;
  }

  get dirtySelectedFieldsCount(): number {
    if (this.fieldSettingsHidden) return 0;

    let count = 0;
    this.fieldSettingsByFlow.forEach((settings) => {
      settings.forEach((fs) => {
        if (<boolean>(<unknown>fs.hasDirtyAttributes) && fs.isSelected) count++;
      });
    });
    return count;
  }

  get dirtySelectedFlowsCount(): number {
    if (this.fieldSettingsHidden) return 0;

    let count = 0;
    this.fieldSettingsByFlow.forEach((settings) => {
      if (settings.some((fs) => <boolean>(<unknown>fs.hasDirtyAttributes) && fs.isSelected)) count++;
    });
    return count;
  }

  get retentionPeriodChanged(): boolean {
    if (!(<boolean>(<unknown>this.args.currentLocation.hasDirtyAttributes))) return false;
    const changed = this.args.currentLocation.changedAttributes();
    return 'entryRetentionPeriod' in changed || 'redactAllVisitorData' in changed;
  }

  get showWarningBanner(): boolean {
    const hasDirtyFields = this.dirtySelectedFieldsCount > 0;
    return hasDirtyFields || this.retentionPeriodChanged;
  }

  open = (): void => {
    this.isOpen = true;
  };

  cancel = (): void => {
    this.args.currentLocation.rollbackAttributes();
    this.#rollbackFieldSettings();
    this.#initFromLocation();
    this.isOpen = false;
  };

  setIndefinitely = (): void => {
    this.args.currentLocation.entryRetentionPeriod = null;
    this.selectedOption = null;
  };

  setRedactAll = (): void => {
    this.args.currentLocation.redactAllVisitorData = true;
  };

  setSelectFields = (): void => {
    this.args.currentLocation.redactAllVisitorData = false;
  };

  setCustomPeriod = (): void => {
    const firstPreset = RETENTION_OPTIONS[0]!;
    this.selectedOption = firstPreset;
    this.args.currentLocation.entryRetentionPeriod = firstPreset.value as number;
    if (!this.flows) {
      void this.loadFlowsTask.perform();
    }
  };

  selectOption = (option: RetentionOption): void => {
    this.selectedOption = option;
    this.customDays = String(this.args.currentLocation.entryRetentionPeriod);

    if (option.value !== 'custom') {
      this.args.currentLocation.entryRetentionPeriod = option.value;
    } else {
      const days = parseInt(this.customDays, 10);
      this.args.currentLocation.entryRetentionPeriod = !isNaN(days) && days > 0 ? days : 30;
    }
  };

  updateCustomDays = (value: string): void => {
    this.customDays = value;
    const days = parseInt(value, 10);

    if (!this.customDaysIsNaN && !this.customDaysTooLow) {
      this.args.currentLocation.entryRetentionPeriod = days;
    }
  };

  selectFlow = (flow: FlowModel): void => {
    this.selectedFlow = flow;
    void this.loadFieldSettingsTask.perform(flow);
  };

  toggleFieldSetting = (fieldSetting: FieldSetting): void => {
    fieldSetting.isSelected = !fieldSetting.isSelected;
  };

  selectAllFields = (): void => {
    this.fieldSettings.forEach((fs) => (fs.isSelected = true));
  };

  deselectAllFields = (): void => {
    this.fieldSettings.forEach((fs) => (fs.isSelected = false));
  };

  handleSaveClick = (): void => {
    const wouldDelete =
      this.#hasNewlySelectedFields() || this.#retentionPeriodShrunk() || this.#redactAllNewlyEnabled();

    if (wouldDelete) {
      this.confirmDeleteText = '';
      this.showDeleteModal = true;
    } else {
      void this.saveTask.perform();
    }
  };

  cancelDeleteModal = (): void => {
    this.confirmDeleteText = '';
    this.showDeleteModal = false;
  };

  updateConfirmDeleteText = (value: string): void => {
    this.confirmDeleteText = value;
  };

  loadFlowsTask = dropTask(async () => {
    this.flows = await this.infinity.model(
      'flow',
      {
        filter: {
          location: this.args.currentLocation.id,
          include_deleted: true,
          // include employee-centric and property-owned flows which are not included unless explicitly requested
          employee_centric: 'true,false',
          property_owned: 'true,false',
        },
        perPage: 20,
        startingPage: 0,
        perPageParam: 'page[limit]',
        pageParam: 'page[offset]',
        countParam: 'meta.total',
      },
      ExtendedInfinityModel,
    );

    const firstFlow = this.flows.slice()[0];
    if (firstFlow) this.selectFlow(firstFlow);
  });

  loadFieldSettingsTask = restartableTask(async (flow: FlowModel) => {
    let fieldSettings = (<FieldSetting[] | null>flow.hasMany('fieldSettings').value())?.slice();
    const hasChanges = fieldSettings?.some((fs) => fs.hasDirtyAttributes);

    if (hasChanges) {
      fieldSettings = (await flow.fieldSettings)?.slice() ?? [];
    } else {
      fieldSettings = (await flow.fieldSettings.reload())?.slice() ?? [];
    }
    // eslint-disable-next-line ember/use-ember-get-and-set
    this.fieldSettingsByFlow.set(flow.id, fieldSettings);
  });

  saveTask = dropTask(async () => {
    const changes = <[number | null, number | null] | undefined>(
      this.args.currentLocation.changedAttributes()['entryRetentionPeriod']
    );
    const [oldRetention, newRetention] = changes ?? [null, null];

    const redactChanges = <[boolean, boolean] | undefined>(
      this.args.currentLocation.changedAttributes()['redactAllVisitorData']
    );
    const [oldRedactAll, newRedactAll] = redactChanges ?? [null, null];

    const { currentLocation } = this.args;
    const flows = this.flows?.slice() ?? [];

    if (<boolean>(<unknown>currentLocation.hasDirtyAttributes)) {
      try {
        await currentLocation.save();
      } catch (e) {
        this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
        return;
      }
    }

    const serialized = flows
      .map((flow) => flow.serializeDirtyFieldSettings())
      .filter((s): s is NonNullable<typeof s> => s !== null);

    // only send PATCH request if there are dirty field settings and we are not setting to indefinitely or discarding all data
    if (serialized.length && !this.indefinitely && !this.args.currentLocation.redactAllVisitorData) {
      try {
        await this.ajaxFetch.request(urlBuilder.v2.fieldSettings(), {
          type: 'PATCH',
          contentType: 'application/json',
          data: JSON.stringify({ field_settings: serialized }),
        });
      } catch (e) {
        if (changes || redactChanges) {
          // rollback location changes if the request fails, since the field settings won't be updated
          if (changes) this.args.currentLocation.entryRetentionPeriod = oldRetention;
          if (redactChanges) this.args.currentLocation.redactAllVisitorData = <boolean>oldRedactAll;
          let rollbackSucceeded = true;
          try {
            await currentLocation.save();
          } catch {
            // rollback save also failed: server is still at the NEW values from the earlier
            // successful save, but local stays at OLD so the form matches the user's pre-edit
            // intent. Divergence resolves on next refresh when canonical reloads from server.
            rollbackSucceeded = false;
          }
          if (rollbackSucceeded) {
            // restore form state to the user's intended values so they can retry the save
            if (changes) this.args.currentLocation.entryRetentionPeriod = newRetention;
            if (redactChanges) this.args.currentLocation.redactAllVisitorData = <boolean>newRedactAll;
          }
        }
        this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
        return;
      }
    }

    // these are reloaded every time into the store and not serialized from the PATCH
    this.#rollbackFieldSettings();
    this.#initFromLocation();

    this.isOpen = false;
    this.confirmDeleteText = '';
    this.showDeleteModal = false;

    this.flashMessages.showAndHideFlash('success', 'Data retention policy updated');
  });

  #rollbackFieldSettings() {
    this.fieldSettingsByFlow.forEach((settings) => {
      settings.forEach((setting) => {
        setting.rollbackAttributes();
      });
    });

    this.fieldSettingsByFlow.clear();

    if (this.selectedFlow) {
      // reload field settings for the selected flow
      void this.loadFieldSettingsTask.perform(this.selectedFlow);
    }
  }

  #initFromLocation(): void {
    this.customDays = '';

    const days = this.args.currentLocation.entryRetentionPeriod;
    if (!days) {
      this.selectedOption = null;
      return;
    }
    if (PRESET_VALUES.has(days)) {
      this.selectedOption = RETENTION_OPTIONS.find((o) => o.value === days) ?? null;
    } else {
      this.selectedOption = RETENTION_OPTIONS.find((o) => o.value === 'custom') ?? null;
      this.customDays = String(days);
    }
  }

  #hasNewlySelectedFields() {
    if (this.indefinitely) return false;

    for (const settings of this.fieldSettingsByFlow.values()) {
      if (settings.some((fs) => <boolean>(<unknown>fs.hasDirtyAttributes) && fs.isSelected)) return true;
    }

    return false;
  }

  #redactAllNewlyEnabled(): boolean {
    if (this.indefinitely) return false;

    const change = <[boolean, boolean] | undefined>(
      this.args.currentLocation.changedAttributes()['redactAllVisitorData']
    );

    if (!change) return false;

    const [oldValue, newValue] = change;
    return !oldValue && newValue;
  }

  #retentionPeriodShrunk() {
    const change = <[number | null, number | null] | undefined>(
      this.args.currentLocation.changedAttributes()['entryRetentionPeriod']
    );

    if (!change) return false;

    const [oldPeriod, newPeriod] = change;

    // if new period is indefinitely, it can't be shrinking
    if (!newPeriod) return false;

    // if old period is indefinitely, any new period would be shrinking
    if (!oldPeriod) return true;

    // compare the two periods and see if it's shrinking
    return newPeriod < oldPeriod;
  }

  <template>
    <SettingsPanel @isOpen={{this.isOpen}} data-test-entry-retention-period as |panel|>
      <panel.header
        @iconUri="/assets/images/features/data-retention.svg"
        @title="Visitor data retention policy"
        @onCancel={{this.cancel}}
        as |header|
      >
        <header.description>
          Specify how long visitor data is retained after a visit.<br />
          Learn more about managing
          <a
            href="https://envoy.help/en/articles/12356182-visitor-data-anonymization"
            target="_blank"
            rel="noopener"
          >data retention →</a>
        </header.description>

        <header.controls>
          {{#if this.isOpen}}
            <UiButton
              class="btn btn--small btn--brand"
              disabled={{this.saveDisabled}}
              {{on "click" this.handleSaveClick}}
              data-test-entry-retention-period-save
            >
              {{if this.saveTask.isRunning "Saving..." "Save"}}
            </UiButton>
          {{else if (can "update data-retention")}}
            <UiButton class="btn btn--small btn--brand" {{on "click" this.open}} data-test-entry-retention-period-edit>
              Edit
            </UiButton>
          {{/if}}
        </header.controls>
      </panel.header>
      <panel.body as |SPContent|>
        <SPContent>
          <p id="retention-period-heading" class="font-bold text-carbon-70 mb-4 mt-10">Retention period</p>
          <div class="space-y-4">
            <div class="space-y-4" role="radiogroup" aria-labelledby="retention-period-heading">
              <label class="flex items-center gap-2 cursor-pointer" data-test-retain-indefinitely>
                <input
                  type="radio"
                  name="entry-retention-policy"
                  class="custom-radio shrink-0"
                  checked={{this.indefinitely}}
                  {{on "change" this.setIndefinitely}}
                />
                <span class="text-sm">Retain data indefinitely</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer" data-test-retain-custom>
                <input
                  type="radio"
                  name="entry-retention-policy"
                  class="custom-radio shrink-0"
                  checked={{not this.indefinitely}}
                  {{on "change" this.setCustomPeriod}}
                />
                <span class="text-sm">Discard data after a set retention period</span>
              </label>
            </div>
            {{#unless this.indefinitely}}
              <div class="flex">
                <UiInputField data-test-retention-period-select @required={{true}} as |Field|>
                  <Field.label>Retain for</Field.label>

                  <PowerSelect
                    @options={{this.retentionOptions}}
                    @selected={{this.selectedOption}}
                    @onChange={{this.selectOption}}
                    @searchEnabled={{false}}
                    as |option|
                  >
                    {{option.label}}
                  </PowerSelect>
                </UiInputField>
                {{#if this.isCustomSelected}}
                  <UiInputField
                    class="ml-4 mt-[22px]"
                    @min="1"
                    @step="1"
                    @type="number"
                    @errors={{this.errors}}
                    data-test-retention-custom-input
                    as |Field|
                  >
                    <div class="flex items-center">
                      <Field.input
                        class="!w-16"
                        @value={{this.customDays}}
                        @update={{this.updateCustomDays}}
                        data-test-retention-days-input
                      />
                      {{#if @currentLocation.entryRetentionPeriod}}
                        <span class="text-sm text-carbon-70 whitespace-nowrap ml-2">{{pluralize
                            @currentLocation.entryRetentionPeriod
                            "day"
                            withoutCount=true
                          }}
                          after visit date</span>
                      {{/if}}
                    </div>
                  </UiInputField>
                {{/if}}
              </div>

              {{#if (can "update field settings data-retention")}}
                <div class="mt-8" data-test-data-to-discard>
                  <p id="data-to-discard-heading" class="font-bold mb-1 text-carbon-70">Data to discard</p>
                  <div class="space-y-4 mb-4" role="radiogroup" aria-labelledby="data-to-discard-heading">
                    <label class="flex items-center gap-2 cursor-pointer" data-test-redact-all-data>
                      <input
                        type="radio"
                        name="redact-all-visitor-data"
                        class="custom-radio shrink-0"
                        checked={{@currentLocation.redactAllVisitorData}}
                        {{on "change" this.setRedactAll}}
                      />
                      <span class="text-sm">Discard all data by default</span>
                    </label>
                    <label class="flex items-center gap-2 cursor-pointer" data-test-redact-select-fields>
                      <input
                        type="radio"
                        name="redact-all-visitor-data"
                        class="custom-radio shrink-0"
                        checked={{not @currentLocation.redactAllVisitorData}}
                        {{on "change" this.setSelectFields}}
                      />
                      <span class="text-sm">Select which fields to discard after set retention period</span>
                    </label>
                  </div>

                  {{#unless @currentLocation.redactAllVisitorData}}
                    <UiInputField data-test-flow-select as |Field|>
                      <Field.label>Sign in flow</Field.label>
                      <PowerSelect
                        @options={{this.flowOptions}}
                        @selected={{this.selectedFlow}}
                        @onChange={{this.selectFlow}}
                        @disabled={{or this.saveTask.isRunning this.loadFlowsTask.isRunning}}
                        @searchEnabled={{false}}
                        @placeholder="Select a flow"
                        class="w-72"
                        as |option|
                      >
                        <div class="flex items-center">
                          <GlobalFlowIcon @show={{option.isGlobalChild}}>
                            {{option.name}}
                          </GlobalFlowIcon>
                          {{#if option.deletedAt}}
                            <span class="ml-1">(Deleted)</span>
                          {{/if}}
                        </div>

                        {{#if (and (eq option this.lastFlow) this.flows.canLoadMore)}}
                          <div class="infinity-loading-log !text-carbon-50">
                            <InfinityLoader
                              @infinityModel={{this.flows}}
                              @hideOnInfinity={{true}}
                              @scrollable=".ember-power-select-options"
                            >
                              <img alt="loading" src="/assets/images/loading-grey.gif" />
                              <span class="loading-text">Loading…</span>
                            </InfinityLoader>
                          </div>
                        {{/if}}
                      </PowerSelect>
                    </UiInputField>

                    {{#if this.selectedFlow}}
                      {{#if this.loadFieldSettingsTask.isRunning}}
                        <p class="text-sm text-carbon-50 mt-4" data-test-field-settings-loading>Loading fields…</p>
                      {{else if this.fieldSettings.length}}
                        <div class="mt-4" data-test-field-settings-list>
                          <div class="flex gap-1 text-sm mb-2 border-b">
                            <span class="font-bold text-carbon-50 mr-1">Data to discard</span>
                            <UiButton
                              {{on "click" this.selectAllFields}}
                              class="text-gem-50"
                              data-test-select-all-fields
                            >
                              Select all
                            </UiButton>
                            <span class="text-carbon-50 font-bold">|</span>
                            <UiButton
                              {{on "click" this.deselectAllFields}}
                              class="text-gem-50"
                              data-test-deselect-all-fields
                            >
                              Deselect all
                            </UiButton>
                          </div>
                          {{#each this.fieldSettings as |fieldSetting|}}
                            <div class="flex flex-col py-1 w-fit" data-test-field-setting>
                              <label class="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  class="shrink-0"
                                  checked={{fieldSetting.isSelected}}
                                  {{on "change" (fn this.toggleFieldSetting fieldSetting)}}
                                />
                                <div class="text-sm flex items-center">
                                  {{#if fieldSetting.isAttachment}}
                                    {{svgJar "attachment" class="mr-1"}}
                                  {{/if}}
                                  <TruncateWithTooltip @value={{fieldSetting.displayName}} @length={{70}} />
                                </div>
                              </label>
                              {{#if fieldSetting.flowVersionId}}
                                <span class="text-xs text-carbon-50 mt-1" data-test-field-setting-version>
                                  Field added by an
                                  <a
                                    href="https://envoy.help/en/articles/13764979-advanced-visitor-approvals#h_633158c5eb"
                                    target="_blank"
                                    rel="noopener"
                                  >
                                    Approval Policy
                                  </a>.
                                </span>
                              {{/if}}
                            </div>
                          {{/each}}
                        </div>
                      {{/if}}
                    {{/if}}
                  {{/unless}}
                </div>
              {{/if}}
            {{/unless}}
          </div>
        </SPContent>
      </panel.body>
    </SettingsPanel>

    {{#if this.showDeleteModal}}
      <ModalDialog
        @clickOutsideToClose={{false}}
        @translucentOverlay={{true}}
        @onClose={{this.cancelDeleteModal}}
        @targetAttachment="center"
        @containerClass="ember-modal-dialog !max-w-[612px]"
      >
        <div class="p-8" data-test-field-settings-delete-modal>
          <h1 class="text-2xl mb-4">Confirm retention policy</h1>
          <p class="text-sm text-carbon-60">
            This selection will apply to past, recent, and future visit data.
          </p>

          <ul class="mb-6 text-sm list-disc ml-4">
            {{#if @currentLocation.entryRetentionPeriod}}
              <li>
                <span class="text-carbon-60">
                  Data in these fields will be deleted
                  {{pluralize @currentLocation.entryRetentionPeriod "day"}}
                  after the visit date.
                </span>
              </li>
            {{/if}}
            {{#if @currentLocation.entryRetentionPeriod}}
              <li>
                <span class="text-papaya-50">
                  Data older than
                  {{pluralize @currentLocation.entryRetentionPeriod "day"}}
                  will be deleted immediately.
                </span>
              </li>
            {{/if}}
          </ul>

          <p class="text-sm text-carbon-60 mb-6">
            Deleted data cannot be recovered, including by Envoy’s support team.
          </p>
          {{#if this.showWarningBanner}}
            <MessageBox @type="warning" class="mb-4" data-test-delete-confirmation-message>
              {{#if this.dirtySelectedFieldsCount}}
                This action will permanently delete
                {{pluralize this.dirtySelectedFieldsCount "newly selected sign in field"}}
                across
                {{pluralize this.dirtySelectedFlowsCount "sign in flow"}}.
              {{/if}}
              Fields already scheduled to be discarded at a future date may be permanently discarded immediately if past
              the new retention period.
            </MessageBox>
          {{/if}}

          <UiInputField @required={{true}} data-test-delete-confirmation-input as |Field|>
            <Field.label>Type
              <strong>delete</strong>
              (lowercase) to confirm</Field.label>
            <Field.input @value={{this.confirmDeleteText}} @update={{this.updateConfirmDeleteText}} />
          </UiInputField>
          <div class="flex justify-end gap-3 mt-6">
            <UiButton
              class="btn btn--small btn--nonHoverFill"
              disabled={{this.saveTask.isRunning}}
              data-test-delete-modal-cancel
              {{on "click" this.cancelDeleteModal}}
            >
              Cancel
            </UiButton>
            <UiButton
              class="btn btn--small btn--brand"
              disabled={{or (not (eq this.confirmDeleteText "delete")) this.saveTask.isRunning}}
              data-test-delete-modal-confirm
              {{on "click" this.saveTask.perform}}
            >
              {{if this.saveTask.isRunning "Saving..." "Confirm and delete"}}
            </UiButton>
          </div>
        </div>
      </ModalDialog>
    {{/if}}
  </template>
}
